/// <amd-module name="Build/Pipelines/Tabs/Branches/BranchesTable" />
import { IBuildLinkingService } from "Build/Client/Linking";
import { getBuildStatusIndicatorData } from "Build/Common/Library/Indicator";
import { formatPipelineRunName } from "Build/Common/Library/PipelineRunNameFormatter";
import * as Resources from "Build/Pipelines/Resources";
import { BranchesViewItem, RichHistogramEntry } from "Build/Pipelines/Tabs/Branches/BranchesDataService";
import { Histogram } from "Build/Shared/Components/Histogram/index";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Pill } from "VSSUI/Pill";
import { Status, StatusSize } from "VSSUI/Status";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TableColumnStyle } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface Props extends IVssComponentProperties {
    branches: BranchesViewItem[];
    definitionId: number;
}

export class BranchesTable extends VssComponent<Props> {
    private columns: ITableColumn<BranchesViewItem>[];
    private sourceProviderService: ISourceProviderService;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        this.columns = [
            {
                id: "title",
                width: 400,
                name: Resources.BranchColumnName,
                className: "pipelines-cell",
                renderCell: this._renderTitleCell,
                columnStyle: TableColumnStyle.Primary
            },
            {
                id: "history",
                width: 240,
                name: Resources.HistoryColumn,
                className: "pipelines-cell",
                renderCell: this._renderHistoryCell
            },
            {
                id: "details",
                width: -100,
                minWidth: 240,
                name: Resources.LastRunColumnName,
                className: "pipelines-cell",
                renderCell: this._renderDetailsCell
            }
        ];

        this.sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
    }

    public render() {
        const provider = new ArrayItemProvider<BranchesViewItem>(this.props.branches);

        return (
            <Card className="bolt-table-card flex-grow" contentProps={{ contentPadding: false, className: "branches-table-scroll" }}>
                <Table ariaLabel={Resources.BranchesTabName} columns={this.columns} itemProvider={provider} onActivate={this._onActivateRow} />
            </Card>
        );
    }

    private _renderTitleCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<BranchesViewItem>,
        tableItem: BranchesViewItem
    ): JSX.Element => {
        const sourceProvider = this.sourceProviderService.getSourceProvider(tableItem.repositoryType);
        const cleanBranchName = sourceProvider ? sourceProvider.getBranchText(tableItem.sourceBranch) : tableItem.sourceBranch;

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <span className="fontWeightSemiBold">{cleanBranchName}</span>
                {tableItem.isDefaultBranch && (
                    <Pill className="margin-left-8" excludeTabStop={true}>
                        {Resources.DefaultBranchLabel}
                    </Pill>
                )}
            </SimpleTableCell>
        );
    };

    private _renderHistoryCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<BranchesViewItem>,
        tableItem: BranchesViewItem
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <Histogram
                    excludeTabStopOnItems={true}
                    className="branches-view-histogram"
                    items={tableItem.builds}
                    ariaLabelForList={Resources.AriaHistogramBuildsList}
                    width={8}
                    height={40}
                    renderTooltip={(item: RichHistogramEntry) => {
                        return (
                            <div className="rhythm-vertical-16">
                                <div>{item.tooltip}</div>
                                {item.usedRepositoryBranches && item.usedRepositoryBranches.length > 1 && (
                                    <div className="flex-column rhythm-vertical-8">
                                        {item.usedRepositoryBranches.map(data => {
                                            const sourceProvider = this.sourceProviderService.getSourceProvider(data.repository.type);
                                            return (
                                                <div key={data.branchId} className="flex-row flex-center rhythm-horizontal-4">
                                                    <Icon iconName={sourceProvider ? sourceProvider.getRepoIconName() : ""} />
                                                    <Tooltip overflowOnly={true}>
                                                        <span className="text-ellipsis">{data.repository.name}</span>
                                                    </Tooltip>
                                                    <Icon
                                                        iconName={sourceProvider ? sourceProvider.getBranchIconName(data.branchName) : "OpenSource"}
                                                    />
                                                    <Tooltip overflowOnly={true}>
                                                        <span className="fontWeightSemiBold text-ellipsis">
                                                            {sourceProvider ? sourceProvider.getBranchText(data.branchName) : data.branchName}
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                    selectRightMost={true}
                    minimumLength={15}
                    noBorderElement={true}
                    noFocusZone={true}
                />
            </SimpleTableCell>
        );
    };

    private _renderDetailsCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<BranchesViewItem>,
        tableItem: BranchesViewItem
    ): JSX.Element => {
        const { latestBuild } = tableItem;
        if (latestBuild) {
            const pageContext = this.context.pageContext;
            const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
            const link = linkingService.getBuildUrl(latestBuild.id);

            const detailText = formatPipelineRunName(
                latestBuild.buildNumber,
                tableItem.sourceVersionMessage,
                tableItem.appendCommitMessageToRunName
            );
            const statusData = getBuildStatusIndicatorData(latestBuild.status, latestBuild.result);
            return (
                <SimpleTableCell
                    columnIndex={columnIndex}
                    tableColumn={tableColumn}
                    key={"col-" + columnIndex}
                    contentClassName="bolt-table-cell-content-with-link scroll-hidden"
                >
                    <Status
                        ariaLabel={statusData.statusProps.ariaLabel || statusData.label}
                        {...statusData.statusProps}
                        className="icon-large-margin"
                        size={StatusSize.m}
                    />
                    <Tooltip overflowOnly={true} text={detailText}>
                        <FPSLink
                            excludeTabStop={true}
                            className="bolt-table-link bolt-table-inline-link fontSizeM text-ellipsis"
                            href={link}
                            removeUnderline={true}
                        >
                            {detailText}
                        </FPSLink>
                    </Tooltip>
                </SimpleTableCell>
            );
        } else {
            return (
                <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    {" "}
                </SimpleTableCell>
            );
        }
    };

    private _onActivateRow = (event: any, row: ITableRow<BranchesViewItem>) => {
        const pageContext = this.context.pageContext;
        const linkingService = pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const activatedItem = this.props.branches[row.index];

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        if (activatedItem) {
            const link = linkingService.getPipelinesUrl(this.props.definitionId, activatedItem.repositoryId, activatedItem.branchId);
            onClickFPS(pageContext, row.data.href || link, true, event);
        }
    };
}
